import { capitalize } from "./general";
import type { IQuantityDisplay } from "@/common/types/quantity";

/**
 * MED-697 — render a quantity in the product's own packaging levels.
 *
 * One carton of two packs of five tablets is stored as 10 and reads "1 Carton".
 * The server does the arithmetic and sends a ready `display` string; this
 * rebuilds it from the structured `parts` only to apply the capitalisation the
 * rest of the UI uses ("1 Carton + 3 Tablets", not "1 carton + 3 tablets").
 *
 * `fallbackBaseUnits` is what to show when there is no reading to render: a
 * product saved with no packaging hierarchy, or a response from a deploy that
 * predates this field. Showing the stored base-unit number is the honest
 * fallback — the alternative is a blank cell where a quantity used to be.
 */
export function formatQuantity(
  display?: IQuantityDisplay | null,
  fallbackBaseUnits?: number | string | null,
): string {
  const parts = display?.parts;

  if (!parts?.length) {
    // An empty holding still names a unit: "0 Cartons", not a bare "0".
    if (display?.highest) {
      return `0 ${pluralPackaging(display.highest.packaging_type, 0)}`;
    }
    // A negative holding, or no hierarchy at all — the server already put the
    // honest string in `display`; only reach past it if even that is missing.
    if (display?.display) return display.display;
    return fallbackBaseUnits === undefined || fallbackBaseUnits === null
      ? "—"
      : String(fallbackBaseUnits);
  }

  return parts
    .map((part) => `${part.quantity} ${pluralPackaging(part.packaging_type, part.quantity)}`)
    .join(" + ");
}

/**
 * One term of a reading: "1 Carton", "3 Tablets". Exported so a cell that
 * renders the headline and the remainder separately still pluralises and
 * capitalises them exactly as the full `display` string does.
 */
export function formatQuantityPart(packagingType: string, quantity: number): string {
  return `${quantity} ${pluralPackaging(packagingType, quantity)}`;
}

/** The packaging level a buyer transacts one batch in — see `readBatchUnit`. */
export interface IBatchUnit {
  /** Capitalised packaging label, e.g. "Carton", "Pack", or "Unit". */
  label: string;
  /** Base units one package at this level holds. Never zero. */
  multiplier: number;
  /** Whole packages of this level in stock — the order ceiling. */
  inStock: number;
}

/**
 * MED-706 — the packaging unit a buyer transacts THIS batch in.
 *
 * MED-697 established that a batch's `quantity_display` is already rendered at
 * the level its holder sells at (a manufacturer's carton, an importer's pack) —
 * not the product's outermost layer. `highest` on that reading carries the
 * three things the order flow needs about that level: the label to show, the
 * base units one package holds (the multiplier for the submit conversion), and
 * how many whole ones are in stock (already floored server-side).
 *
 * `ReqForm`, `all-products-order` and `product-order-content` each derived this
 * independently from the product's own `packaging_layers[0]` + `base_unit_count`,
 * which is only correct for a manufacturer. This is the one place that reads it.
 *
 * Falls back to a base unit with a multiplier of one when the product has no
 * packaging hierarchy (`highest` is null).
 */
export function readBatchUnit(display?: IQuantityDisplay | null): IBatchUnit {
  const highest = display?.highest;

  if (!highest) {
    return {
      label: "Unit",
      multiplier: 1,
      inStock: Math.max(0, Math.floor(Number(display?.base_units) || 0)),
    };
  }

  return {
    label: capitalize(highest.packaging_type) || "Unit",
    multiplier: highest.base_units_per || 1,
    inStock: Math.max(0, highest.quantity || 0),
  };
}

/**
 * "Carton" / "Cartons". The server sends correctly pluralised English in
 * `display`; this is only for the strings rebuilt here from `parts`. Packaging
 * types are a fixed list of single words on the backend (carton, pack, box,
 * case, tray, ...), so the -es rule for sibilant endings covers all of them.
 */
function pluralPackaging(packagingType: string, count: number): string {
  const name = capitalize(packagingType);
  if (count === 1) return name;
  return /(s|x|z|ch|sh)$/i.test(name) ? `${name}es` : `${name}s`;
}
